/**
 * Единые токены оформления чата.
 *
 * Зачем файл: раньше каждый компонент чата задавал свои радиусы, отступы и
 * цвета прямо в sx — из-за этого список, полотно сообщений, шапка и модалки
 * выглядели как четыре разные системы. Здесь одно место, где живут размеры,
 * скругления, тайминги анимаций и палитра аватаров.
 *
 * Правило: компоненты чата не хардкодят числа, а берут их отсюда.
 */

// ── Размеры ───────────────────────────────────────────────────────────
export const CHAT = {
  // Ширина колонки со списком. Раньше в MessagesPage стояло
  // minmax(min-content, min(30%, 400px)) — min-content зависел от самой
  // длинной строки в карточке чата, поэтому колонка разъезжалась далеко
  // за 400px. Теперь ширина фиксирована и от контента не зависит.
  listWidth: 328,
  listWidthLg: 360,

  rowHeight: 72,
  avatarSm: 34,
  avatarMd: 44,
  avatarLg: 84,

  // Пузырь сообщения
  bubbleRadius: 18,
  bubbleTailRadius: 6,
  bubbleMaxWidth: 'min(560px, 74%)',
  bubblePadX: 12,
  bubblePadY: 8,

  // Композер
  composerRadius: 22,
  // Шапка чата. Было 64 — вместе с отступами полоса выходила под 80px
  // и заметно съедала полотно. В мессенджерах это 52-56.
  headerHeight: 54,
};

// ── Тайминги анимаций ─────────────────────────────────────────────────
export const MOTION = {
  fast: 0.16,
  base: 0.24,
  slow: 0.38,
  // Полёт сообщения от поля ввода к ленте
  flight: 0.62,
  spring: { type: 'spring', stiffness: 420, damping: 32, mass: 0.7 },
  softSpring: { type: 'spring', stiffness: 260, damping: 26 },
  ease: [0.22, 1, 0.36, 1],      // easeOutExpo — основной
  easeIn: [0.55, 0, 1, 0.45],
};

// ── Реакции ───────────────────────────────────────────────────────────
// Набор должен совпадать с MessageReaction.EMOJI_CHOICES на сервере:
// сервер принимает только эти значения и на прочие отвечает 400.
export const REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🔥'];

// ── Иконки ────────────────────────────────────────────────────────────
/**
 * MUI Joy красит иконку внутри IconButton через переменную --Icon-color,
 * а не через обычный color. Поэтому `sx={{ color: '#fff' }}` на кнопке
 * иконку НЕ перекрашивал — она оставалась тёмной (именно так и получались
 * чёрные стрелка «отправить» и значок «ответить» на фиолетовом фоне).
 * Красим правильно — через саму переменную.
 */
export const iconColor = (value) => ({
  '--Icon-color': value,
  color: value,
  '& svg': { color: value },
});

// ── Чип даты в ленте ──────────────────────────────────────────────────
// Тёмная пилюля с БЕЛЫМ текстом. Фон НЕПРОЗРАЧНЫЙ намеренно: пока чипы были
// полупрозрачными и липли все разом, их фоны складывались и пилюля темнела
// до нечитаемости. Теперь липнет ровно один чип (см. dayChipWrap), но
// плотный фон гарантирует читаемость и поверх фотографии.
export const dateChip = {
  px: 1.5,
  py: 0.5,
  borderRadius: 12,
  backgroundColor: 'rgba(24,32,56,0.92)',
  boxShadow: '0 2px 10px rgba(16,24,40,0.18)',
  color: '#fff',
  fontWeight: 700,
  fontSize: 11.5,
  lineHeight: 1.5,
  letterSpacing: '0.02em',
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
};

/**
 * Обёртка липкого чипа даты.
 *
 * Главное здесь — ГДЕ живёт sticky. Лента разбита на секции по дням, и чип
 * липнет внутри СВОЕЙ секции. Поэтому, доехав до конца своего дня, он
 * выталкивается следующим — ровно как в мессенджерах. Когда все чипы висели
 * в общем потоке, каждый липнул к одному и тому же верху, они наслаивались
 * друг на друга и темнели.
 *
 * pointerEvents: none — чип не должен перехватывать клики по сообщениям.
 */
export const dayChipWrap = {
  position: 'sticky',
  top: 4,
  zIndex: 3,
  display: 'flex',
  justifyContent: 'center',
  pointerEvents: 'none',
  py: 0.75,
};

// ── Разделитель непрочитанного ────────────────────────────────────────
// Линия с подписью посередине: всё, что ниже неё, человек ещё не читал.
export const unreadDivider = (theme) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  my: 1.25,
  color: theme.palette.mode === 'dark' ? '#a5b4fc' : '#4f46e5',
  fontSize: 11.5,
  fontWeight: 700,
  letterSpacing: '0.02em',
  '&::before, &::after': {
    content: '""',
    flex: 1,
    height: '1px',
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(165,180,252,0.35)' : 'rgba(99,102,241,0.30)',
  },
});

// ── Палитра аватаров ──────────────────────────────────────────────────
// Восемь спокойных градиентов. Цвет выбирается по имени — у одного человека
// он всегда одинаковый, в списке и в шапке.
const AVATAR_COLORS = [
  ['#6366f1', '#8b5cf6'],
  ['#0ea5e9', '#2563eb'],
  ['#10b981', '#059669'],
  ['#f59e0b', '#ea580c'],
  ['#ec4899', '#db2777'],
  ['#14b8a6', '#0d9488'],
  ['#8b5cf6', '#6d28d9'],
  ['#ef4444', '#dc2626'],
];

function hashString(value) {
  const str = String(value || '');
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

/** Градиент аватара по имени/идентификатору. */
export function avatarGradient(seed) {
  const [from, to] = AVATAR_COLORS[hashString(seed) % AVATAR_COLORS.length];
  return `linear-gradient(135deg, ${from} 0%, ${to} 100%)`;
}

/** Плоский цвет аватара — для мест, где градиент неуместен (бордюры, точки). */
export function avatarColor(seed) {
  return AVATAR_COLORS[hashString(seed) % AVATAR_COLORS.length][0];
}

/** Инициалы: «Иванов Пётр» → «ИП», «Событие #25705 · Тула» → «С». */
export function initialsOf(name) {
  const clean = String(name || '').trim();
  if (!clean) return '?';
  const words = clean
    .replace(/[#№·•]/g, ' ')
    .split(/\s+/)
    .filter((w) => /[A-Za-zА-Яа-яЁё]/.test(w));
  if (words.length === 0) return clean[0].toUpperCase();
  if (words.length === 1) return words[0][0].toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

// ── Полотно чата ──────────────────────────────────────────────────────
/**
 * Фон ленты сообщений. Мягкий градиент плюс еле заметная сетка точек —
 * чтобы белые пузыри не сливались с фоном, как это было раньше.
 * Работает и в светлой, и в тёмной теме.
 */
export const canvasBackground = (theme) => {
  const dark = theme.palette.mode === 'dark';
  const base = dark
    ? 'radial-gradient(120% 90% at 50% 0%, #1b2233 0%, #141823 60%, #11141d 100%)'
    : 'radial-gradient(120% 90% at 50% 0%, #f3f5fb 0%, #eef1f8 55%, #eaeef7 100%)';
  const dot = dark ? 'rgba(255,255,255,0.028)' : 'rgba(22,34,71,0.035)';
  return {
    backgroundImage: `radial-gradient(${dot} 1px, transparent 1px), ${base}`,
    backgroundSize: '22px 22px, auto',
  };
};

/** Стиль «своего» пузыря. */
export const ownBubble = (theme) => ({
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, #4f46e5 0%, #6d3fd6 100%)'
    : 'linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)',
  color: '#fff',
  boxShadow: '0 2px 10px rgba(79,70,229,0.28)',
});

/** Стиль «чужого» пузыря. */
export const otherBubble = (theme) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#232838' : '#ffffff',
  color: theme.palette.text.primary,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 1px 2px rgba(0,0,0,0.4)'
    : '0 1px 2px rgba(16,24,40,0.07), 0 1px 3px rgba(16,24,40,0.05)',
});

// ── Дата и время ──────────────────────────────────────────────────────
const MONTHS = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

/** «14:32» — время в пузыре. */
export function bubbleTime(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Ключ дня — по нему группируем сообщения под разделителем. */
export function dayKey(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'unknown';
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** «Сегодня» / «Вчера» / «14 сентября» / «14 сентября 2024». */
export function dayLabel(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (diffDays === 0) return 'Сегодня';
  if (diffDays === 1) return 'Вчера';
  const sameYear = d.getFullYear() === now.getFullYear();
  return `${d.getDate()} ${MONTHS[d.getMonth()]}${sameYear ? '' : ` ${d.getFullYear()}`}`;
}

/** Компактное время для списка чатов: «14:32», «вчера», «14.09». */
export function listTime(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (diffDays === 0) return bubbleTime(value);
  if (diffDays === 1) return 'вчера';
  if (diffDays < 7) return ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'][d.getDay()];
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return d.getFullYear() === now.getFullYear() ? `${dd}.${mm}` : `${dd}.${mm}.${String(d.getFullYear()).slice(2)}`;
}

/** Короткое описание вложений для превью в списке: «Фото», «3 файла». */
export function attachmentsLabel(attachments) {
  const list = attachments || [];
  if (list.length === 0) return '';
  if (list.some((a) => a?.type === 'voice')) return 'Голосовое сообщение';
  if (list.length === 1) {
    if (list[0]?.type === 'photo') return 'Фото';
    return list[0]?.name || 'Файл';
  }
  const n = list.length;
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} файл`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return `${n} файла`;
  return `${n} файлов`;
}

/** Склонение «участник». */
export function participantsLabel(n) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} участник`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return `${n} участника`;
  return `${n} участников`;
}

// ── Однострочный эллипсис ─────────────────────────────────────────────
// Раньше длинные адреса в карточке чата растягивали колонку: у контейнеров
// не было minWidth:0, и flex-элемент не мог стать уже своего содержимого.
export const ellipsis = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/** Многострочный обрез (N строк). */
export const clampLines = (lines = 2) => ({
  minWidth: 0,
  display: '-webkit-box',
  WebkitLineClamp: String(lines),
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  wordBreak: 'break-word',
});

// ── Полоса прокрутки ──────────────────────────────────────────────────
// Тонкая, появляется при наведении — как в телеграме.
export const thinScrollbar = (theme) => ({
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : 'rgba(22,34,71,0.18)'} transparent`,
  '&::-webkit-scrollbar': { width: 8, height: 8 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 8,
    border: '2px solid transparent',
    backgroundClip: 'content-box',
    backgroundColor: 'transparent',
    transition: 'background-color .2s',
  },
  '&:hover::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(22,34,71,0.2)',
  },
});

// ── Должности ─────────────────────────────────────────────────────────
// Один справочник на весь чат. До него коды сыпались в интерфейс как есть:
// в подсказке @упоминаний под именем стояло «surveyor, draftsman,
// quality_master», и понять, кто это, можно было только догадкой.
export const POSITION_LABELS = {
  surveyor:       'Обмерщик',
  draftsman:      'Чертёжник',
  manager:        'Менеджер',
  head_manager:   'Старший менеджер',
  quality_master: 'Контроль качества',
  administrator:  'Администратор',
};

/**
 * Подпись под именем участника: должности по-русски, клиенту — «Клиент».
 * Неизвестный код возвращаем как есть — лучше увидеть его, чем пустоту и
 * гадать, почему у человека нет подписи.
 */
export function positionLabel(position, kind) {
  if (kind === 'client') return 'Клиент';
  const list = Array.isArray(position) ? position : (position ? [position] : []);
  const out = list.map((p) => POSITION_LABELS[p] || p).filter(Boolean);
  return out.join(', ');
}
